import { useState, useEffect, useRef } from "react";
import {
  ArrowLeft,
  Plus,
  Users,
  Calendar,
  Target,
  TrendingUp,
  Download,
  Check,
  Upload,
  Eye,
  FileText,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import OrganicShape from "../components/Board/OrganicShape";
import AnnualWheel from "../components/Board/AnnualWheel";
import InteractiveAnnualWheel from "../components/Board/InteractiveAnnualWheel";
import CorporateEventsPanel from "../components/Board/CorporateEventsPanel";
import { DEFAULT_FILTER } from "../components/Board/InteractiveAnnualWheel";
import DocumentManager from "../components/Board/DocumentManager";
import BoardMemberGallery from "../components/Board/BoardMemberGallery";
import CompanyEvents from "../components/Board/CompanyEvents";
import BoardPerformance from "../components/Board/BoardPerformance";
import BoardDynamicsNotification from "../components/Board/BoardDynamicsNotification";
import BoardActionsPanel from "../components/Board/BoardActionsPanel";
import BoardSettingsPanel from "../components/Board/BoardSettingsPanel";
import { GovernanceProvider } from "../contexts/GovernanceContext";
import {
  fetchCommittees,
  fetchDocuments,
  uploadDocument,
  downloadDocument,
  deleteDocument,
  fetchBoardMembers,
  createCommittee,
  addCommitteeMember,
} from "../services/businessDashApi";
import { dispatchBusinessDashNavigate } from "../lib/boardActionsNavigate";

const Board = ({ onStorageLimitReached }) => {
  // Year filter for Board - Time and Planning section
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [wheelFilter, setWheelFilter] = useState(DEFAULT_FILTER);

  const [selectedCommittee, setSelectedCommittee] = useState(null);
  const [isCommitteeDialogOpen, setIsCommitteeDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isDocumentDialogOpen, setIsDocumentDialogOpen] = useState(false);
  const [selectedExecutive, setSelectedExecutive] = useState(null);
  const [isExecutiveDialogOpen, setIsExecutiveDialogOpen] = useState(false);
  const [showAddRoleDialog, setShowAddRoleDialog] = useState(false);
  const [showReorganizeDialog, setShowReorganizeDialog] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showDynamicsNotification, setShowDynamicsNotification] =
    useState(false); // Changed to false - notification checks backend automatically
  // New states for CRUD operations
  const [showMilestoneForm, setShowMilestoneForm] = useState(false);
  const [showMeetingForm, setShowMeetingForm] = useState(false);
  const [showKpiForm, setShowKpiForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingItemType, setEditingItemType] = useState(null);
  const [showAddCommitteeForm, setShowAddCommitteeForm] = useState(false);
  const [editingPurpose, setEditingPurpose] = useState(false);
  const [purposeText, setPurposeText] = useState("");
  const [viewingDocument, setViewingDocument] = useState(null);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);

  // File upload refs for governance frameworks
  const boardCharterFileRef = useRef(null);
  const dutiesOverviewFileRef = useRef(null);
  const codeOfConductFileRef = useRef(null);
  const frameworkUploadRef = useRef(null); // For uploaded documents section

  // API Loading States
  const [committeeData, setCommitteeData] = useState({});
  const [isLoadingCommittees, setIsLoadingCommittees] = useState(true);
  const [committeeLoadError, setCommitteeLoadError] = useState(null);

  // Load committees from API
  useEffect(() => {
    const loadCommittees = async () => {
      try {
        setIsLoadingCommittees(true);
        setCommitteeLoadError(null);
        const committees = await fetchCommittees();

        // Transform API response to component state format
        const transformedData = {};
        committees.forEach((committee) => {
          transformedData[committee.name] = {
            id: committee.id,
            startDate: committee.start_date || committee.startDate || "",
            deadline: committee.end_date || committee.deadline || "Ongoing",
            members: committee.members || committee.member_list || [],
            meetings: committee.meetings || committee.meeting_list || [],
            documents: committee.documents || committee.document_list || [],
            purpose: committee.purpose || "",
            kpis: committee.kpis || committee.kpi_list || [],
            milestones: committee.milestones || committee.milestone_list || [],
          };
        });

        setCommitteeData(transformedData);
      } catch (error) {
        console.error("Failed to load committees:", error);
        setCommitteeLoadError("Failed to load committees. Please try again.");
      } finally {
        setIsLoadingCommittees(false);
      }
    };

    loadCommittees();
  }, []);

  // Keep static data as fallback (remove this section later)
  const [_oldCommitteeData] = useState({
    "Circular Economy": {
      startDate: "15-03-2024",
      deadline: "30-12-2024",
      members: [
        {
          name: "John Smith",
          image:
            "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
        },
        {
          name: "Sarah Johnson",
          image: "/lovable-uploads/aa2423e1-7f80-498a-9d58-b377e2266f62.png",
        },
        {
          name: "Michael Chen",
          image:
            "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
        },
      ],
      meetings: [
        { date: "15-03-2024", type: "Kickoff Meeting" },
        { date: "22-04-2024", type: "Progress Review" },
        { date: "10-06-2024", type: "Strategic Planning" },
      ],
      documents: [
        { name: "Circular_Economy_Framework.pdf", type: "pdf", size: "2.4 MB" },
        { name: "Implementation_Roadmap.xlsx", type: "xlsx", size: "856 KB" },
      ],
      purpose:
        "Drive sustainable business practices and circular economy initiatives within the organization.",
      kpis: [
        { name: "Waste Reduction", target: "30%", current: "18%" },
        { name: "Circular Revenue", target: "€2M", current: "€1.2M" },
      ],
      milestones: [
        {
          name: "Sustainability Framework",
          status: "Completed",
          date: "30-04-2024",
        },
        {
          name: "Circular Business Model",
          status: "In Progress",
          date: "31-08-2024",
        },
        { name: "Implementation Plan", status: "Planned", date: "31-12-2024" },
      ],
    },
    "New Business Models": {
      startDate: "22-05-2024",
      deadline: "31-12-2024",
      members: [
        {
          name: "Emma Davis",
          image: "/lovable-uploads/0c187ea9-50a0-4ea3-856e-b22b5943f73a.png",
        },
        {
          name: "John Smith",
          image:
            "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
        },
      ],
      meetings: [
        { date: "22-05-2024", type: "Formation Meeting" },
        { date: "15-07-2024", type: "Market Analysis" },
      ],
      documents: [
        { name: "Business_Model_Canvas.pdf", type: "pdf", size: "1.8 MB" },
        { name: "Market_Analysis_Q2.xlsx", type: "xlsx", size: "1.2 MB" },
      ],
      purpose:
        "Explore and develop innovative business models to drive future growth and market expansion.",
      kpis: [
        { name: "New Revenue Streams", target: "3", current: "1" },
        { name: "Market Penetration", target: "15%", current: "8%" },
      ],
      milestones: [
        { name: "Market Research", status: "Completed", date: "30-06-2024" },
        {
          name: "Business Model Design",
          status: "In Progress",
          date: "30-09-2024",
        },
      ],
    },
    Funding: {
      startDate: "10-01-2024",
      deadline: "30-09-2024",
      members: [
        {
          name: "Emma Davis",
          image: "/lovable-uploads/0c187ea9-50a0-4ea3-856e-b22b5943f73a.png",
        },
        {
          name: "Sarah Johnson",
          image: "/lovable-uploads/aa2423e1-7f80-498a-9d58-b377e2266f62.png",
        },
        {
          name: "Michael Chen",
          image:
            "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
        },
        {
          name: "John Smith",
          image:
            "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
        },
      ],
      meetings: [
        { date: "10-01-2024", type: "Planning Session" },
        { date: "28-02-2024", type: "Investor Pitch Prep" },
        { date: "15-04-2024", type: "Due Diligence" },
        { date: "30-06-2024", type: "Final Review" },
      ],
      documents: [
        { name: "Funding_Strategy_2024.pdf", type: "pdf", size: "3.2 MB" },
        { name: "Investor_Pitch_Deck.pptx", type: "pptx", size: "15.8 MB" },
        { name: "Grant_Applications.xlsx", type: "xlsx", size: "2.1 MB" },
      ],
      purpose:
        "Secure strategic funding and investment opportunities to support company growth and expansion initiatives.",
      kpis: [
        { name: "Funding Secured", target: "€5M", current: "€3.2M" },
        { name: "Investor Meetings", target: "20", current: "15" },
      ],
      milestones: [
        {
          name: "Series A Preparation",
          status: "Completed",
          date: "31-03-2024",
        },
        {
          name: "Investor Presentations",
          status: "Completed",
          date: "30-06-2024",
        },
        { name: "Funding Closure", status: "In Progress", date: "31-08-2024" },
      ],
    },
  });

  // Governance Frameworks Loading
  const [governanceFrameworks, setGovernanceFrameworks] = useState({});
  const [isLoadingFrameworks, setIsLoadingFrameworks] = useState(true);

  useEffect(() => {
    const loadFrameworks = async () => {
      try {
        setIsLoadingFrameworks(true);
        // Fetch framework documents from documents API instead of governance-frameworks API
        const frameworkDocs = await fetchDocuments({
          document_type: "framework",
        });

        // Transform the documents into the expected format
        const frameworks = transformFrameworkDocuments(frameworkDocs);

        setGovernanceFrameworks(frameworks);
      } catch (error) {
        console.error("Failed to load governance frameworks:", error);
      } finally {
        setIsLoadingFrameworks(false);
      }
    };

    loadFrameworks();
  }, []);

  // Keep static fallback
  const [_oldGovernanceFrameworks] = useState({
    "Board Effectiveness": {
      description:
        "Framework for evaluating and improving board performance and effectiveness",
      components: [
        "Board Evaluation Matrix",
        "Skills Assessment",
        "Performance Metrics",
      ],
      lastUpdated: "2024-03-15",
      documents: [
        { name: "Board_Evaluation_Framework.pdf", type: "pdf", size: "2.8 MB" },
        { name: "Skills_Assessment_Matrix.xlsx", type: "xlsx", size: "1.5 MB" },
        { name: "Performance_Dashboard.png", type: "png", size: "850 KB" },
      ],
    },
    "Risk Management": {
      description:
        "Comprehensive risk identification and mitigation framework for board oversight",
      components: [
        "Risk Register",
        "Mitigation Strategies",
        "Monitoring Dashboard",
      ],
      lastUpdated: "2024-02-28",
      documents: [
        { name: "Risk_Management_Framework.pdf", type: "pdf", size: "3.4 MB" },
        { name: "Risk_Register_2024.xlsx", type: "xlsx", size: "2.1 MB" },
        { name: "Mitigation_Strategies.docx", type: "docx", size: "1.2 MB" },
      ],
    },
    "Stakeholder Engagement": {
      description:
        "Framework for systematic engagement with key stakeholders and interest groups",
      components: [
        "Stakeholder Mapping",
        "Engagement Plan",
        "Feedback Mechanisms",
      ],
      lastUpdated: "2024-04-10",
      documents: [
        { name: "Stakeholder_Map.pdf", type: "pdf", size: "1.9 MB" },
        { name: "Engagement_Strategy.pptx", type: "pptx", size: "8.7 MB" },
        { name: "Feedback_Analysis.xlsx", type: "xlsx", size: "3.2 MB" },
      ],
    },
    "Strategic Planning": {
      description:
        "Structured approach to long-term strategic planning and execution monitoring",
      components: ["Strategic Framework", "KPI Dashboard", "Review Processes"],
      lastUpdated: "2024-01-20",
      documents: [
        { name: "Strategic_Plan_2024-2026.pdf", type: "pdf", size: "4.1 MB" },
        { name: "KPI_Dashboard.xlsx", type: "xlsx", size: "2.8 MB" },
        { name: "Strategy_Review_Process.docx", type: "docx", size: "980 KB" },
      ],
    },
  });

  const handleCommitteeClick = (committeeName) => {
    setSelectedCommittee(committeeName);
    setIsCommitteeDialogOpen(true);
  };

  const handleDocumentClick = (documentName) => {
    setSelectedDocument(documentName);
    setIsDocumentDialogOpen(true);
  };

  const handleExecutiveClick = (executiveName) => {
    setSelectedExecutive(executiveName);
    setIsExecutiveDialogOpen(true);
  };

  // Helper function to transform framework documents into the expected format
  const transformFrameworkDocuments = (frameworkDocs) => {
    return {
      "Board Charter": {
        description:
          "Defines the board's purpose, scope, and relationship with management. Establishes clear boundaries and expectations for board oversight and strategic guidance.",
        components: [
          "Board purpose and mission statement",
          "Scope of authority and decision-making",
          "Management relationship framework",
        ],
        lastUpdated: "2024-03-15",
        documents: frameworkDocs.filter(
          (doc) => doc.frameworkCategory === "Board Charter"
        ),
      },
      "Duties Overview": {
        description:
          "Clearly outlines the duties and responsibilities of the chair, committee chairs, and individual directors to prevent confusion and increase accountability.",
        components: [
          "Chair responsibilities and leadership role",
          "Committee chair duties and reporting",
          "Individual director obligations",
        ],
        lastUpdated: "2024-03-15",
        documents: frameworkDocs.filter(
          (doc) => doc.frameworkCategory === "Duties Overview"
        ),
      },
      "Code of Conduct": {
        description:
          "Establishes ethical standards, behavioral expectations, and compliance requirements for all board members and executives.",
        components: [
          "Ethical standards and principles",
          "Conflict of interest policies",
          "Compliance and reporting procedures",
        ],
        lastUpdated: "2024-03-15",
        documents: frameworkDocs.filter(
          (doc) => doc.frameworkCategory === "Code of Conduct"
        ),
      },
    };
  };

  // Governance framework document handlers
  const handleFrameworkFileUpload = async (event, frameworkName) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type (PDF only for governance docs)
    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "pdf") {
      alert("Only PDF files are allowed for governance documents");
      return;
    }

    // Validate file size
    if (file.size > 50 * 1024 * 1024) {
      alert("File too large (max 50MB)");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", file.name);
      formData.append("document_type", "framework");
      formData.append("frameworkCategory", frameworkName); // Add specific framework category
      formData.append("year", new Date().getFullYear());

      await uploadDocument(formData);

      // Refresh governance frameworks by fetching framework documents
      const frameworkDocs = await fetchDocuments({
        document_type: "framework",
      });
      const frameworks = transformFrameworkDocuments(frameworkDocs);
      setGovernanceFrameworks(frameworks);

      console.log(`✅ ${frameworkName} document uploaded successfully!`);
      alert("Document uploaded successfully!");
    } catch (error) {
      console.error("Failed to upload framework document:", error);
      alert("Failed to upload document. Please try again.");
    }
  };

  const handleFrameworkDocDownload = async (docName, docId) => {
    try {
      // If docId exists, use it; otherwise this is a static document
      if (docId) {
        const blob = await downloadDocument(docId);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", docName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        console.log("✅ Document downloaded:", docName);
      } else {
        alert(
          "This is a sample document. Upload your own version to download it."
        );
      }
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download document");
    }
  };

  const handleFrameworkDocDelete = async (docId) => {
    if (!window.confirm("Are you sure you want to delete this document?")) {
      return;
    }

    try {
      await deleteDocument(docId);

      // Refresh governance frameworks after deletion
      const frameworkDocs = await fetchDocuments({
        document_type: "framework",
      });
      const frameworks = transformFrameworkDocuments(frameworkDocs);
      setGovernanceFrameworks(frameworks);

      console.log("✅ Document deleted successfully");
      alert("Document deleted successfully!");
    } catch (error) {
      console.error("Delete failed:", error);
      alert("Failed to delete document");
    }
  };

  // CRUD handlers for committee items
  const handleAddMilestone = (milestone) => {
    if (selectedCommittee) {
      setCommitteeData((prev) => ({
        ...prev,
        [selectedCommittee]: {
          ...prev[selectedCommittee],
          milestones: [...prev[selectedCommittee].milestones, milestone],
        },
      }));
    }
  };

  const handleEditMilestone = (index, milestone) => {
    if (selectedCommittee) {
      setCommitteeData((prev) => ({
        ...prev,
        [selectedCommittee]: {
          ...prev[selectedCommittee],
          milestones: prev[selectedCommittee].milestones.map((item, i) =>
            i === index ? milestone : item
          ),
        },
      }));
    }
  };

  const handleDeleteMilestone = (index) => {
    if (selectedCommittee) {
      setCommitteeData((prev) => ({
        ...prev,
        [selectedCommittee]: {
          ...prev[selectedCommittee],
          milestones: prev[selectedCommittee].milestones.filter(
            (_, i) => i !== index
          ),
        },
      }));
    }
  };

  const handleAddMeeting = (meeting) => {
    if (selectedCommittee) {
      setCommitteeData((prev) => ({
        ...prev,
        [selectedCommittee]: {
          ...prev[selectedCommittee],
          meetings: [...prev[selectedCommittee].meetings, meeting],
        },
      }));
    }
  };

  const handleEditMeeting = (index, meeting) => {
    if (selectedCommittee) {
      setCommitteeData((prev) => ({
        ...prev,
        [selectedCommittee]: {
          ...prev[selectedCommittee],
          meetings: prev[selectedCommittee].meetings.map((item, i) =>
            i === index ? meeting : item
          ),
        },
      }));
    }
  };

  const handleDeleteMeeting = (index) => {
    if (selectedCommittee) {
      setCommitteeData((prev) => ({
        ...prev,
        [selectedCommittee]: {
          ...prev[selectedCommittee],
          meetings: prev[selectedCommittee].meetings.filter(
            (_, i) => i !== index
          ),
        },
      }));
    }
  };

  const handleAddKpi = (kpi) => {
    if (selectedCommittee) {
      setCommitteeData((prev) => ({
        ...prev,
        [selectedCommittee]: {
          ...prev[selectedCommittee],
          kpis: [...prev[selectedCommittee].kpis, kpi],
        },
      }));
    }
  };

  const handleEditKpi = (index, kpi) => {
    if (selectedCommittee) {
      setCommitteeData((prev) => ({
        ...prev,
        [selectedCommittee]: {
          ...prev[selectedCommittee],
          kpis: prev[selectedCommittee].kpis.map((item, i) =>
            i === index ? kpi : item
          ),
        },
      }));
    }
  };

  const handleDeleteKpi = (index) => {
    if (selectedCommittee) {
      setCommitteeData((prev) => ({
        ...prev,
        [selectedCommittee]: {
          ...prev[selectedCommittee],
          kpis: prev[selectedCommittee].kpis.filter((_, i) => i !== index),
        },
      }));
    }
  };

  const handleAddCommittee = (committee) => {
    // Convert participant names to member objects
    const members = Array.isArray(committee.participants) 
      ? committee.participants.map(name => ({ name, type: "board" }))
      : [];
    
    setCommitteeData((prev) => ({
      ...prev,
      [committee.name]: {
        startDate: committee.startDate,
        deadline: committee.deadline,
        members: members,
        meetings: [],
        documents: [],
        purpose: committee.purpose,
        kpis: [],
        milestones: [],
      },
    }));
  };

  // Board members for signatures (loaded from API)
  const [boardMembers, setBoardMembers] = useState([]);
  const [isLoadingBoardMembers, setIsLoadingBoardMembers] = useState(true);

  // Load board members from API
  useEffect(() => {
    const loadBoardMembers = async () => {
      try {
        setIsLoadingBoardMembers(true);
        const members = await fetchBoardMembers();
        // Filter to only approved and active members
        const approvedMembers = (Array.isArray(members) ? members : (members.data || [])).filter(
          (member) => member.approval_status === "approved" && member.is_active
        );
        // Transform API data to include signature status (you can add this to backend later)
        const membersWithSignatures = approvedMembers.map((member) => ({
          id: member.id,
          name: member.name,
          image:
            member.profile_picture ||
            member.image ||
            "https://images.unsplash.com/photo-1581092795360-fd1ca04f0952?w=150&h=150&fit=crop&crop=face",
          signed: member.signed || false, // Will be from backend eventually
          signedDate: member.signed_date || null, // Will be from backend eventually
        }));
        setBoardMembers(membersWithSignatures);
      } catch (error) {
        console.error("Failed to load board members:", error);
        // Keep empty array on error
      } finally {
        setIsLoadingBoardMembers(false);
      }
    };
    loadBoardMembers();
  }, []);

  const documentContent = {
    "Board Charter": {
      title: "Board Charter",
      pdfName: "board_charter_2024.pdf",
      content: `
        BOARD CHARTER
        
        1. PURPOSE
        The Board of Directors provides strategic oversight and governance to ensure the company operates with integrity, accountability, and in the best interests of all stakeholders.
        
        2. COMPOSITION
        The Board shall consist of 5-9 directors, with a majority being independent directors.
        
        3. RESPONSIBILITIES
        • Strategic planning and oversight
        • Risk management and compliance
        • CEO selection and evaluation
        • Financial oversight and reporting
        • Stakeholder relations
        
        4. MEETINGS
        The Board shall meet at least quarterly, with additional meetings as needed.
        
        5. COMMITTEES
        The Board may establish committees to assist in fulfilling its responsibilities.
      `,
    },
    "Duties Overview": {
      title: "Board Duties Overview",
      pdfName: "board_duties_overview_2024.pdf",
      content: `
        BOARD DUTIES OVERVIEW
        
        BOARD CHAIR RESPONSIBILITIES:
        • Lead board meetings and set agendas
        • Facilitate effective board discussions
        • Serve as primary liaison with CEO
        • Ensure board effectiveness and governance
        
        COMMITTEE CHAIR DUTIES:
        • Oversee committee activities and meetings
        • Report committee findings to full board
        • Coordinate with management on committee matters
        
        INDIVIDUAL DIRECTOR OBLIGATIONS:
        • Attend board and committee meetings
        • Prepare thoroughly for all meetings
        • Act with care, loyalty, and good faith
        • Maintain confidentiality of board matters
      `,
    },
    "Code of Conduct": {
      title: "Board Code of Conduct",
      pdfName: "board_code_of_conduct_2024.pdf",
      content: `
        BOARD CODE OF CONDUCT
        
        1. ETHICAL STANDARDS
        All board members must act with honesty, integrity, and ethical conduct.
        
        2. CONFLICTS OF INTEREST
        Directors must disclose any potential conflicts and recuse themselves from related decisions.
        
        3. CONFIDENTIALITY
        Board discussions and materials are confidential and must not be disclosed without authorization.
        
        4. COMPLIANCE
        Directors must comply with all applicable laws, regulations, and company policies.
        
        5. ACCOUNTABILITY
        Each director is accountable for their actions and decisions as a board member.
        
        6. PROFESSIONAL CONDUCT
        Directors must maintain professional behavior and respect for all stakeholders.
      `,
    },
    "Conflict of Interest Policy": {
      title: "Conflict of Interest Policy",
      pdfName: "conflict_of_interest_policy_2024.pdf",
      content: `
        CONFLICT OF INTEREST POLICY
        
        1. DEFINITION
        A conflict of interest occurs when a director's personal interests could impair their ability to act in the company's best interests.
        
        2. DISCLOSURE REQUIREMENTS
        Directors must annually disclose all potential conflicts and update as situations change.
        
        3. PROCEDURES
        • Identify potential conflicts before board discussions
        • Disclose conflicts to the board
        • Recuse from discussion and voting on affected matters
        
        4. DOCUMENTATION
        All conflicts and related actions must be documented in board minutes.
        
        5. COMPLIANCE
        Violations may result in removal from the board.
      `,
    },
  };

  // Executive team data
  const executiveTeam = {
    CEO: {
      name: "Alexandra Thompson",
      title: "Chief Executive Officer",
      image:
        "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=150&h=150&fit=crop&crop=face",
      tenure: "3 years, 2 months",
      responsibilities: [
        "Strategic leadership",
        "Board liaison",
        "Stakeholder relations",
        "Overall company performance",
      ],
      authorityLevel: "Full executive authority",
      reportsTo: "Board of Directors",
      directReports: ["COO", "CFO", "CTO", "CMO", "CHRO"],
    },
    COO: {
      name: "Michael Rodriguez",
      title: "Chief Operating Officer",
      image:
        "https://images.unsplash.com/photo-1581092795360-fd1ca04f0952?w=150&h=150&fit=crop&crop=face",
      tenure: "2 years, 8 months",
      responsibilities: [
        "Daily operations",
        "Process optimization",
        "Supply chain management",
        "Quality assurance",
      ],
      authorityLevel: "Operational decisions up to €500K",
      reportsTo: "CEO",
      directReports: [
        "Operations Manager",
        "Supply Chain Director",
        "Quality Manager",
      ],
    },
    CFO: {
      name: "Sarah Chen",
      title: "Chief Financial Officer",
      image:
        "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=150&h=150&fit=crop&crop=face",
      tenure: "4 years, 1 month",
      responsibilities: [
        "Financial planning",
        "Risk management",
        "Investor relations",
        "Compliance oversight",
      ],
      authorityLevel: "Financial decisions up to €1M",
      reportsTo: "CEO",
      directReports: [
        "Controller",
        "Treasury Director",
        "Internal Audit Manager",
      ],
    },
    CTO: {
      name: "David Kim",
      title: "Chief Technology Officer",
      image:
        "https://images.unsplash.com/photo-1581092795360-fd1ca04f0952?w=150&h=150&fit=crop&crop=face",
      tenure: "1 year, 6 months",
      responsibilities: [
        "Technology strategy",
        "Product development",
        "IT infrastructure",
        "Innovation initiatives",
      ],
      authorityLevel: "Technology investments up to €300K",
      reportsTo: "CEO",
      directReports: ["Engineering Manager", "Product Manager", "IT Director"],
    },
    CMO: {
      name: "Emma Foster",
      title: "Chief Marketing Officer",
      image:
        "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=150&h=150&fit=crop&crop=face",
      tenure: "2 years, 3 months",
      responsibilities: [
        "Brand management",
        "Customer acquisition",
        "Market research",
        "Digital marketing",
      ],
      authorityLevel: "Marketing spend up to €200K",
      reportsTo: "CEO",
      directReports: [
        "Brand Manager",
        "Digital Marketing Manager",
        "Market Research Analyst",
      ],
    },
    CHRO: {
      name: "James Wilson",
      title: "Chief Human Resources Officer",
      image:
        "https://images.unsplash.com/photo-1581092795360-fd1ca04f0952?w=150&h=150&fit=crop&crop=face",
      tenure: "3 years, 7 months",
      responsibilities: [
        "Talent management",
        "Culture development",
        "Compensation & benefits",
        "Employee relations",
      ],
      authorityLevel: "HR decisions up to €150K",
      reportsTo: "CEO",
      directReports: [
        "HR Business Partners",
        "Talent Acquisition Manager",
        "L&D Manager",
      ],
    },
  };

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-6 pb-12">
        {/* Back to Home Button */}
        <Button
          variant="ghost"
          className="mt-6 mb-6 hover:bg-primary/10"
          onClick={() => window.history.back()}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>

        {/* Board Label and Hero Section */}
        <div className="flex flex-col xl:flex-row items-start gap-8 mb-12">
          {/* Left side: Board Label and Hero Text */}
          <div className="flex flex-col lg:flex-row items-start gap-8 flex-1">
            {/* Vertical Board Label - Wider */}
            <div className="flex flex-col items-center gap-4 lg:w-1/6">
              <OrganicShape className="p-6 flex flex-col items-center gap-3 h-48 w-full">
                <h1 className="text-xl font-semibold text-foreground tracking-wide writing-mode-vertical lg:writing-mode-horizontal">
                  BOARD
                </h1>
                <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-white"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                  </svg>
                </div>
              </OrganicShape>
            </div>

            {/* Hero Text - No longer interactive */}
            <OrganicShape
              variant="secondary"
              className="flex-1 p-6 bg-gradient-hero min-h-50"
            >
              <div className="space-y-4 h-full flex flex-col justify-center">
                <h2 className="text-xl font-semibold text-foreground">
                  What does the board need from us?
                </h2>

                <div className="space-y-3">
                  <p className="text-sm text-foreground/90 leading-relaxed">
                    Good boards need diverse skills, clear roles, and strong
                    ethics. They work best when members attend meetings, share
                    different expertise, and follow ethical rules. Our tracking
                    shows attendance rates, skill mix, education levels, and
                    rule compliance. This helps ensure the board makes smart
                    decisions, manages risks well, and guides the company
                    properly.
                  </p>

                  <p className="text-sm text-foreground/80 leading-relaxed">
                    We provide economy reports, financial summaries, department
                    status updates, challenges and positive news highlights, and
                    annual meeting schedules to support effective board
                    oversight.
                  </p>
                </div>
              </div>
            </OrganicShape>
          </div>
        </div>

        {/* Board Member Gallery - Horizontal Layout */}
        <div className="mb-12">
          <h3 className="text-2xl font-semibold text-foreground mb-8">
            Board Members
          </h3>
          <BoardMemberGallery />
        </div>

        {/* Board Actions and Settings */}
        <GovernanceProvider>
          <div className="mb-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <BoardActionsPanel
                onNavigateToGovernance={(section) => {
                  const governanceSection =
                    section === "pulse"
                      ? "pulse"
                      : section === "training"
                        ? "training"
                        : "chemistry";
                  dispatchBusinessDashNavigate({
                    tab: "governance",
                    governanceSection,
                  });
                }}
                onNavigateToMeeting={() => {
                  const el = document.getElementById("board-meeting-section");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                onNavigateToChairSummary={(surveyType) => {
                  const st = String(surveyType || "").toLowerCase().replace(/_/g, "-");
                  const governanceSection =
                    st === "governance-pulse" || st === "governancepulse"
                      ? "pulse"
                      : "chemistry";
                  dispatchBusinessDashNavigate({
                    tab: "governance",
                    governanceSection,
                  });
                }}
                onMarkResultsReviewed={(actionId) => {
                  console.log("Marked as reviewed:", actionId);
                }}
              />
              <BoardSettingsPanel />
            </div>
          </div>
        </GovernanceProvider>

        {/* Board Performance Section */}
        <BoardPerformance />

        {/* Committees Section */}
        <div className="mb-12">
          <h3 className="text-2xl font-semibold text-foreground mb-8">
            Committees
          </h3>
          <div className="flex items-start justify-start gap-6">
            {/* Committee Labels */}
            <div
              className="flex flex-col items-center p-6 bg-gradient-subtle backdrop-blur-sm rounded-soft border border-primary/20 shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
              onClick={() => handleCommitteeClick("Circular Economy")}
            >
              <h4 className="text-lg font-semibold mb-3 text-primary">
                Circular Economy
              </h4>
              <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mb-3 relative">
                <div className="w-6 h-6 border-2 border-primary rounded-full border-dashed"></div>
                <div className="absolute -bottom-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                  3
                </div>
              </div>
              <p className="text-sm text-foreground/80 font-medium mb-2">
                Drive sustainable business practices
              </p>
              <p className="text-sm text-foreground/80 font-medium">
                15-03-2024 - 30-12-2024
              </p>
            </div>

            <div
              className="flex flex-col items-center p-6 bg-gradient-subtle backdrop-blur-sm rounded-soft border border-primary/20 shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
              onClick={() => handleCommitteeClick("New Business Models")}
            >
              <h4 className="text-lg font-semibold mb-3 text-primary">
                New Business Models
              </h4>
              <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mb-3 relative">
                <div className="w-6 h-6 border-2 border-primary rounded-full border-dashed"></div>
                <div className="absolute -bottom-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                  2
                </div>
              </div>
              <p className="text-sm text-foreground/80 font-medium mb-2">
                Explore innovative business models
              </p>
              <p className="text-sm text-foreground/80 font-medium">
                22-05-2024 - 31-12-2024
              </p>
            </div>

            <div
              className="flex flex-col items-center p-6 bg-gradient-subtle backdrop-blur-sm rounded-soft border border-primary/20 shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
              onClick={() => handleCommitteeClick("Funding")}
            >
              <h4 className="text-lg font-semibold mb-3 text-primary">
                Funding
              </h4>
              <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mb-3 relative">
                <div className="w-6 h-6 border-2 border-primary rounded-full border-dashed"></div>
                <div className="absolute -bottom-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                  4
                </div>
              </div>
              <p className="text-sm text-foreground/80 font-medium mb-2">
                Secure strategic funding opportunities
              </p>
              <p className="text-sm text-foreground/80 font-medium">
                10-01-2024 - 30-09-2024
              </p>
            </div>

            {/* Add Committee Button */}
            <div
              className="flex flex-col items-center p-6 bg-card/30 backdrop-blur-sm rounded-soft border border-dashed border-muted/40 cursor-pointer hover:border-primary/40 transition-colors"
              onClick={() => setShowAddCommitteeForm(true)}
            >
              <Plus className="w-8 h-8 text-muted-foreground hover:text-primary/60 transition-colors" />
              <p className="text-sm text-muted-foreground mt-2">
                Add Committee
              </p>
            </div>
          </div>
        </div>

        {/* Board - Time and Planning Section */}
        <div id="board-meeting-section" className="mb-12">
          <h3 className="text-2xl font-semibold text-foreground mb-8">
            Board - Time and Planning
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-4 lg:items-start">
            {/* Annual Wheel — column width follows the wheel; avoids empty strip beside Corporate Events */}
            <div className="min-w-0 w-fit max-w-full">
              <OrganicShape className="p-6 bg-gradient-subtle hover:shadow-lg transition-all duration-300 hover:scale-[1.01] hover:shadow-primary/20 w-fit max-w-full">
                <InteractiveAnnualWheel
                  onFilterChange={(filter) => setWheelFilter(filter)}
                  activeFilter={wheelFilter}
                />
              </OrganicShape>
            </div>
            <div className="min-w-0">
              <CorporateEventsPanel activeFilter={wheelFilter} />
            </div>
          </div>
        </div>

        {/* Board Governance Framework */}
        <div className="mb-12">
          <h3 className="text-2xl font-semibold text-foreground mb-8">
            Board Governance Framework
          </h3>
          <div className="grid lg:grid-cols-2 gap-8">
            <div
              className="group cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-primary/20"
              onClick={() => handleDocumentClick("Board Charter")}
            >
              <OrganicShape className="p-6 bg-gradient-subtle hover:bg-gradient-to-br hover:from-primary/10 hover:to-primary/5 transition-all duration-300">
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <Users className="w-6 h-6 text-primary" />
                      <h4 className="text-lg font-semibold text-foreground">
                        Board Charter
                      </h4>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <FileText className="w-4 h-4 text-primary" />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-1 h-8 w-8"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1 h-8 w-8"
                          >
                            <Upload className="w-4 h-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-md">
                          <DialogHeader>
                            <DialogTitle>Upload Document</DialogTitle>
                            <DialogDescription>
                              Upload PDF documents only for Board Charter
                            </DialogDescription>
                          </DialogHeader>
                          <input
                            ref={boardCharterFileRef}
                            type="file"
                            accept=".pdf"
                            onChange={(e) =>
                              handleFrameworkFileUpload(e, "Board Charter")
                            }
                            className="hidden"
                          />
                          <div
                            className="border-2 border-dashed border-muted rounded-soft p-6 text-center bg-muted/20 cursor-pointer hover:border-primary/40 transition-colors"
                            onClick={() => boardCharterFileRef.current?.click()}
                          >
                            <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                            <p className="text-sm text-muted-foreground mb-3">
                              Click to choose PDF file
                            </p>
                            <Button
                              variant="outline"
                              size="sm"
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                boardCharterFileRef.current?.click();
                              }}
                            >
                              Choose Files
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                  <p className="text-sm text-foreground/80">
                    Defines the board's purpose, scope, and relationship with
                    management. Establishes clear boundaries and expectations
                    for board oversight and strategic guidance.
                  </p>
                  <div className="bg-primary/10 rounded-soft p-3">
                    <ul className="text-xs space-y-1">
                      <li>• Board purpose and mission statement</li>
                      <li>• Scope of authority and decision-making</li>
                      <li>• Management relationship framework</li>
                    </ul>
                  </div>
                </div>
              </OrganicShape>
            </div>

            <div
              className="group cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-primary/20"
              onClick={() => handleDocumentClick("Duties Overview")}
            >
              <OrganicShape className="p-6 bg-gradient-subtle hover:bg-gradient-to-br hover:from-primary/10 hover:to-primary/5 transition-all duration-300">
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <Target className="w-6 h-6 text-primary" />
                      <h4 className="text-lg font-semibold text-foreground">
                        Duties Overview
                      </h4>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <FileText className="w-4 h-4 text-primary" />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-1 h-8 w-8"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1 h-8 w-8"
                          >
                            <Upload className="w-4 h-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-md">
                          <DialogHeader>
                            <DialogTitle>Upload Document</DialogTitle>
                            <DialogDescription>
                              Upload PDF documents only for Duties Overview
                            </DialogDescription>
                          </DialogHeader>
                          <input
                            ref={dutiesOverviewFileRef}
                            type="file"
                            accept=".pdf"
                            onChange={(e) =>
                              handleFrameworkFileUpload(e, "Duties Overview")
                            }
                            className="hidden"
                          />
                          <div
                            className="border-2 border-dashed border-muted rounded-soft p-6 text-center bg-muted/20 cursor-pointer hover:border-primary/40 transition-colors"
                            onClick={() =>
                              dutiesOverviewFileRef.current?.click()
                            }
                          >
                            <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                            <p className="text-sm text-muted-foreground mb-3">
                              Click to choose PDF file
                            </p>
                            <Button
                              variant="outline"
                              size="sm"
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                dutiesOverviewFileRef.current?.click();
                              }}
                            >
                              Choose Files
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                  <p className="text-sm text-foreground/80">
                    Clearly outlines the duties and responsibilities of the
                    chair, committee chairs, and individual directors to prevent
                    confusion and increase accountability.
                  </p>
                  <div className="bg-primary/10 rounded-soft p-3">
                    <ul className="text-xs space-y-1">
                      <li>• Chair responsibilities and leadership role</li>
                      <li>• Committee chair duties and reporting</li>
                      <li>• Individual director obligations</li>
                    </ul>
                  </div>
                </div>
              </OrganicShape>
            </div>

            <div
              className="group cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-primary/20"
              onClick={() => handleDocumentClick("Code of Conduct")}
            >
              <OrganicShape className="p-6 bg-gradient-subtle hover:bg-gradient-to-br hover:from-primary/10 hover:to-primary/5 transition-all duration-300">
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <TrendingUp className="w-6 h-6 text-primary" />
                      <h4 className="text-lg font-semibold text-foreground">
                        Code of Conduct
                      </h4>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <FileText className="w-4 h-4 text-primary" />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-1 h-8 w-8"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1 h-8 w-8"
                          >
                            <Upload className="w-4 h-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-md">
                          <DialogHeader>
                            <DialogTitle>Upload Document</DialogTitle>
                            <DialogDescription>
                              Upload PDF documents only for Code of Conduct
                            </DialogDescription>
                          </DialogHeader>
                          <input
                            ref={codeOfConductFileRef}
                            type="file"
                            accept=".pdf"
                            onChange={(e) =>
                              handleFrameworkFileUpload(e, "Code of Conduct")
                            }
                            className="hidden"
                          />
                          <div
                            className="border-2 border-dashed border-muted rounded-soft p-6 text-center bg-muted/20 cursor-pointer hover:border-primary/40 transition-colors"
                            onClick={() =>
                              codeOfConductFileRef.current?.click()
                            }
                          >
                            <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                            <p className="text-sm text-muted-foreground mb-3">
                              Click to choose PDF file
                            </p>
                            <Button
                              variant="outline"
                              size="sm"
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                codeOfConductFileRef.current?.click();
                              }}
                            >
                              Choose Files
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                  <p className="text-sm text-foreground/80">
                    Establishes and cultivates a culture that values honesty,
                    integrity, and ethical conduct across all board activities
                    and decision-making.
                  </p>
                  <div className="bg-primary/10 rounded-soft p-3">
                    <ul className="text-xs space-y-1">
                      <li>• Ethical standards and principles</li>
                      <li>• Professional conduct expectations</li>
                      <li>• Integrity and transparency requirements</li>
                    </ul>
                  </div>
                </div>
              </OrganicShape>
            </div>

            <div
              className="group cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-primary/20"
              onClick={() => handleDocumentClick("Conflict of Interest Policy")}
            >
              <OrganicShape className="p-6 bg-gradient-subtle hover:bg-gradient-to-br hover:from-primary/10 hover:to-primary/5 transition-all duration-300">
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-6 h-6 text-primary" />
                      <h4 className="text-lg font-semibold text-foreground">
                        Conflict of Interest Policy
                      </h4>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <FileText className="w-4 h-4 text-primary" />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-1 h-8 w-8"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1 h-8 w-8"
                          >
                            <Upload className="w-4 h-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-md">
                          <DialogHeader>
                            <DialogTitle>Upload Document</DialogTitle>
                            <DialogDescription>
                              Upload PDF documents only for Conflict of Interest
                              Policy
                            </DialogDescription>
                          </DialogHeader>
                          <div className="border-2 border-dashed border-muted rounded-soft p-6 text-center bg-muted/20">
                            <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                            <p className="text-sm text-muted-foreground mb-3">
                              PDF format only
                            </p>
                            <Button variant="outline" size="sm">
                              Choose Files
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                  <p className="text-sm text-foreground/80">
                    Implements a clear policy for identifying and managing
                    conflicts of interest to maintain board independence and
                    ethical decision-making.
                  </p>
                  <div className="bg-primary/10 rounded-soft p-3">
                    <ul className="text-xs space-y-1">
                      <li>• Conflict identification procedures</li>
                      <li>• Disclosure and reporting requirements</li>
                      <li>• Management and resolution protocols</li>
                    </ul>
                  </div>
                </div>
              </OrganicShape>
            </div>
          </div>
        </div>

        {/* Executive Team Structure */}
        <div className="mb-12">
          <div className="flex justify-between items-center mb-8">
            <h3 className="text-2xl font-semibold text-foreground">
              Executive Team Structure
            </h3>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="hover:scale-105 transition-transform"
                onClick={() => setShowAddRoleDialog(true)}
              >
                Add Role
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="hover:scale-105 transition-transform"
                onClick={() => setShowReorganizeDialog(true)}
              >
                Reorganize
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="hover:scale-105 transition-transform"
                onClick={() => setShowTemplateDialog(true)}
              >
                Template
              </Button>
            </div>
          </div>
          <div className="grid gap-8">
            {/* CEO at top */}
            <div className="flex justify-center">
              <div
                className="cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-primary/20"
                onClick={() => handleExecutiveClick("CEO")}
              >
                <OrganicShape className="p-6 bg-gradient-subtle hover:bg-gradient-to-br hover:from-primary/10 hover:to-primary/5 transition-all duration-300">
                  <div className="flex flex-col items-center space-y-3">
                    <div className="w-16 h-16 rounded-full overflow-hidden">
                      <img
                        src={executiveTeam.CEO.image}
                        alt={executiveTeam.CEO.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="text-center">
                      <h4 className="font-semibold text-foreground">
                        {executiveTeam.CEO.name}
                      </h4>
                      <p className="text-sm text-foreground/80">
                        {executiveTeam.CEO.title}
                      </p>
                    </div>
                  </div>
                </OrganicShape>
              </div>
            </div>

            {/* Connecting line */}
            <div className="flex justify-center">
              <div className="w-px h-8 bg-primary/30"></div>
            </div>

            {/* C-Suite executives */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-6">
              {["COO", "CFO", "CTO", "CMO", "CHRO"].map((role) => (
                <div key={role} className="flex flex-col items-center">
                  {/* Connecting line from top */}
                  <div className="w-px h-6 bg-primary/30 mb-2"></div>

                  <div
                    className="cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-primary/20"
                    onClick={() => handleExecutiveClick(role)}
                  >
                    <OrganicShape className="p-4 bg-gradient-subtle hover:bg-gradient-to-br hover:from-primary/10 hover:to-primary/5 transition-all duration-300">
                      <div className="flex flex-col items-center space-y-2">
                        <div className="w-12 h-12 rounded-full overflow-hidden">
                          <img
                            src={executiveTeam[role].image}
                            alt={executiveTeam[role].name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="text-center">
                          <h5 className="text-sm font-medium text-foreground">
                            {executiveTeam[role].name}
                          </h5>
                          <p className="text-xs text-foreground/70">{role}</p>
                        </div>
                      </div>
                    </OrganicShape>
                  </div>
                </div>
              ))}
            </div>

            {/* Organizational flexibility note */}
            <div className="text-center mt-4">
              <p className="text-sm text-foreground/60 italic">
                Structure can be adapted based on organization size and industry
                requirements
              </p>
            </div>
          </div>
        </div>

        {/* Committee Details Dialog */}
        <Dialog
          open={isCommitteeDialogOpen}
          onOpenChange={setIsCommitteeDialogOpen}
        >
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-background">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <Users className="w-5 h-5 text-primary" />
                {selectedCommittee} Committee Overview
              </DialogTitle>
              <DialogDescription>
                Detailed overview of the {selectedCommittee} committee including
                members, meetings, and progress.
              </DialogDescription>
            </DialogHeader>

            {selectedCommittee && committeeData[selectedCommittee] && (
              <div className="grid grid-cols-2 gap-6 p-4">
                {/* Left Column */}
                <div className="space-y-6">
                  {/* Committee Info */}
                  <div>
                    <h4 className="font-medium text-foreground mb-2">
                      Committee Details
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">
                          Start Date:
                        </span>
                        <p className="text-foreground">
                          {committeeData[selectedCommittee].startDate}
                        </p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Deadline:</span>
                        <p className="text-foreground">
                          {committeeData[selectedCommittee].deadline}
                        </p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Members:</span>
                        <p className="text-foreground">
                          {committeeData[selectedCommittee].members.length}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Purpose */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-foreground">Purpose</h4>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-1 h-7 w-7"
                        onClick={() => {
                          if (selectedCommittee) {
                            setPurposeText(
                              committeeData[selectedCommittee].purpose
                            );
                            setEditingPurpose(true);
                          }
                        }}
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                    </div>
                    {editingPurpose ? (
                      <div className="space-y-2">
                        <Textarea
                          value={purposeText}
                          onChange={(e) => setPurposeText(e.target.value)}
                          className="min-h-[80px]"
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => {
                              if (selectedCommittee) {
                                setCommitteeData((prev) => ({
                                  ...prev,
                                  [selectedCommittee]: {
                                    ...prev[selectedCommittee],
                                    purpose: purposeText,
                                  },
                                }));
                                setEditingPurpose(false);
                              }
                            }}
                          >
                            Save
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingPurpose(false)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-foreground/80">
                        {selectedCommittee &&
                          committeeData[selectedCommittee].purpose}
                      </p>
                    )}
                  </div>

                  {/* Timeline */}
                  <div>
                    <h4 className="font-medium text-foreground mb-3">
                      Project Timeline
                    </h4>
                    <div className="relative">
                      <div className="absolute left-2 top-0 bottom-0 w-0.5 bg-primary/20"></div>
                      <div className="space-y-4">
                        <div className="flex items-center gap-3">
                          <div className="w-4 h-4 bg-primary rounded-full border-2 border-background"></div>
                          <div className="text-sm">
                            <span className="font-medium">Start: </span>
                            <span className="text-muted-foreground">
                              {committeeData[selectedCommittee].startDate}
                            </span>
                          </div>
                        </div>
                        {committeeData[selectedCommittee].meetings.map(
                          (meeting, idx) => (
                            <div key={idx} className="flex items-center gap-3">
                              <div className="w-4 h-4 bg-secondary rounded-full border-2 border-background"></div>
                              <div className="text-sm">
                                <span className="font-medium">
                                  {meeting.type}:{" "}
                                </span>
                                <span className="text-muted-foreground">
                                  {meeting.date}
                                </span>
                              </div>
                            </div>
                          )
                        )}
                        <div className="flex items-center gap-3">
                          <div className="w-4 h-4 bg-accent rounded-full border-2 border-background"></div>
                          <div className="text-sm">
                            <span className="font-medium">Deadline: </span>
                            <span className="text-muted-foreground">
                              {committeeData[selectedCommittee].deadline}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Documents */}
                  <div>
                    <h4 className="font-medium text-foreground mb-3">
                      Committee Documents
                    </h4>
                    <div className="space-y-2">
                      {committeeData[selectedCommittee].documents?.map(
                        (doc, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between p-2 bg-muted/20 rounded"
                          >
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm">{doc.name}</span>
                              <span className="text-xs text-muted-foreground">
                                ({doc.size})
                              </span>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 w-7 p-0"
                                title="View"
                                onClick={() => {
                                  setViewingDocument(doc);
                                  setShowDocumentViewer(true);
                                }}
                              >
                                <Eye className="w-3 h-3" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 w-7 p-0"
                                title="Download"
                              >
                                <Download className="w-3 h-3" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                                title="Delete"
                              >
                                <svg
                                  className="w-3 h-3"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </Button>
                            </div>
                          </div>
                        )
                      ) || []}
                      <div className="border-2 border-dashed border-muted rounded p-3 text-center bg-muted/10">
                        <Upload className="w-5 h-5 text-muted-foreground mx-auto mb-1" />
                        <p className="text-xs text-muted-foreground mb-2">
                          Upload documents
                        </p>
                        <Button variant="outline" size="sm">
                          Choose Files
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Members */}
                  <div>
                    <h4 className="font-semibold text-foreground border-b pb-2 mb-3">
                      Committee Members
                    </h4>
                    <div className="flex flex-wrap gap-3">
                      {committeeData[selectedCommittee].members.map(
                        (member, index) => (
                          <div
                            key={index}
                            className="flex flex-col items-center space-y-1"
                          >
                            <div className="w-12 h-12 rounded-full overflow-hidden">
                              <img
                                src={member.image}
                                alt={member.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <span className="text-xs text-center">
                              {member.name}
                            </span>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  {/* Meetings */}
                  <div>
                    <h4 className="font-semibold text-foreground border-b pb-2 mb-3">
                      Meetings Held
                    </h4>
                    <div className="space-y-2">
                      {committeeData[selectedCommittee].meetings.map(
                        (meeting, index) => (
                          <div
                            key={index}
                            className="flex justify-between items-center p-2 bg-muted/20 rounded"
                          >
                            <span className="text-sm">{meeting.type}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-foreground/60">
                                {meeting.date}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="p-1 h-7 w-7"
                                onClick={() => {
                                  setEditingItem({ ...meeting, index });
                                  setEditingItemType("meeting");
                                  setShowMeetingForm(true);
                                }}
                              >
                                <Edit className="w-3 h-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="p-1 h-7 w-7 text-destructive hover:text-destructive"
                                onClick={() => handleDeleteMeeting(index)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        )
                      )}
                      <div className="flex justify-center mt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-full w-8 h-8 p-0"
                          onClick={() => {
                            setEditingItem(null);
                            setEditingItemType("meeting");
                            setShowMeetingForm(true);
                          }}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* KPIs */}
                  <div>
                    <h4 className="font-semibold text-foreground border-b pb-2 mb-3">
                      Target KPIs
                    </h4>
                    <div className="space-y-3">
                      {committeeData[selectedCommittee].kpis.map(
                        (kpi, index) => (
                          <div key={index} className="space-y-1">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium">
                                {kpi.name}
                              </span>
                              <div className="flex items-center gap-2">
                                <TrendingUp className="w-4 h-4 text-primary" />
                                <span className="text-xs">
                                  {kpi.current} / {kpi.target}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="p-1 h-7 w-7"
                                  onClick={() => {
                                    setEditingItem({ ...kpi, index });
                                    setEditingItemType("kpi");
                                    setShowKpiForm(true);
                                  }}
                                >
                                  <Edit className="w-3 h-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="p-1 h-7 w-7 text-destructive hover:text-destructive"
                                  onClick={() => handleDeleteKpi(index)}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                            <div className="w-full bg-muted rounded-full h-2">
                              <div
                                className="bg-primary rounded-full h-2 transition-all"
                                style={{
                                  width: `${Math.min(
                                    100,
                                    (parseFloat(
                                      kpi.current.replace(/[^\d.]/g, "")
                                    ) /
                                      parseFloat(
                                        kpi.target.replace(/[^\d.]/g, "")
                                      )) *
                                      100
                                  )}%`,
                                }}
                              ></div>
                            </div>
                          </div>
                        )
                      )}
                      <div className="flex justify-center mt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-full w-8 h-8 p-0"
                          onClick={() => {
                            setEditingItem(null);
                            setEditingItemType("kpi");
                            setShowKpiForm(true);
                          }}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Milestones */}
                  <div>
                    <h4 className="font-semibold text-foreground border-b pb-2 mb-3">
                      Milestone Overview
                    </h4>
                    <div className="space-y-2">
                      {committeeData[selectedCommittee].milestones.map(
                        (milestone, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-2 bg-muted/20 rounded"
                          >
                            <div className="flex items-center gap-2">
                              <Target className="w-4 h-4 text-primary" />
                              <span className="text-sm">{milestone.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span
                                className={`text-xs px-2 py-1 rounded ${
                                  milestone.status === "Completed"
                                    ? "bg-green-100 text-green-700"
                                    : milestone.status === "In Progress"
                                    ? "bg-yellow-100 text-yellow-700"
                                    : "bg-gray-100 text-gray-700"
                                }`}
                              >
                                {milestone.status}
                              </span>
                              <span className="text-xs text-foreground/60">
                                {milestone.date}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="p-1 h-7 w-7"
                                onClick={() => {
                                  setEditingItem({ ...milestone, index });
                                  setEditingItemType("milestone");
                                  setShowMilestoneForm(true);
                                }}
                              >
                                <Edit className="w-3 h-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="p-1 h-7 w-7 text-destructive hover:text-destructive"
                                onClick={() => handleDeleteMilestone(index)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        )
                      )}
                      <div className="flex justify-center mt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-full w-8 h-8 p-0"
                          onClick={() => {
                            setEditingItem(null);
                            setEditingItemType("milestone");
                            setShowMilestoneForm(true);
                          }}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Document Details Dialog */}
        <Dialog
          open={isDocumentDialogOpen}
          onOpenChange={setIsDocumentDialogOpen}
        >
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-background">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <Users className="w-5 h-5 text-primary" />
                {selectedDocument}
              </DialogTitle>
              <DialogDescription>
                View and download documents, and upload new files for this
                governance framework.
              </DialogDescription>
            </DialogHeader>

            {selectedDocument && (
              <div className="space-y-6 p-4">
                {/* Uploaded Documents Overview */}
                <div>
                  <h4 className="font-medium text-foreground mb-4">
                    Uploaded Documents
                  </h4>
                  <div className="space-y-3">
                    {governanceFrameworks[selectedDocument]?.documents?.length >
                    0 ? (
                      governanceFrameworks[selectedDocument].documents.map(
                        (doc) => (
                          <div
                            key={doc.id}
                            className="flex items-center justify-between p-3 bg-muted/20 rounded-soft border"
                          >
                            <div className="flex items-center gap-3">
                              <FileText className="w-5 h-5 text-primary" />
                              <div>
                                <p className="text-sm font-medium">
                                  {doc.name}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Uploaded on {doc.uploadDate} • {doc.size}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="p-1 h-8 w-8"
                                title="Download"
                                onClick={() =>
                                  handleFrameworkDocDownload(doc.name, doc.id)
                                }
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="p-1 h-8 w-8 text-destructive hover:text-destructive"
                                title="Delete"
                                onClick={() => handleFrameworkDocDelete(doc.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        )
                      )
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        No documents uploaded yet. Use the button below to
                        upload documents.
                      </div>
                    )}

                    {/* Upload new document button */}
                    <Dialog>
                      <DialogTrigger asChild>
                        <div className="flex justify-center mt-4">
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-full w-10 h-10 p-0"
                            title="Upload new document"
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                          <DialogTitle>Upload New Document</DialogTitle>
                          <DialogDescription>
                            Upload PDF documents only for {selectedDocument}
                          </DialogDescription>
                        </DialogHeader>
                        <input
                          ref={frameworkUploadRef}
                          type="file"
                          accept=".pdf"
                          onChange={(e) =>
                            handleFrameworkFileUpload(e, selectedDocument)
                          }
                          className="hidden"
                        />
                        <div
                          className="border-2 border-dashed border-muted rounded-soft p-6 text-center bg-muted/20 cursor-pointer hover:border-primary/40 transition-colors"
                          onClick={() => frameworkUploadRef.current?.click()}
                        >
                          <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                          <p className="text-sm text-muted-foreground mb-3">
                            Click to choose PDF file
                          </p>
                          <Button
                            variant="outline"
                            size="sm"
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              frameworkUploadRef.current?.click();
                            }}
                          >
                            Choose Files
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>

                {/* Framework Documents if it's a governance framework */}
                {/* {governanceFrameworks[selectedDocument] && (
                  <div>
                    <h4 className="font-medium text-foreground mb-3">
                      Framework Components
                    </h4>
                    <div className="space-y-2">
                      {governanceFrameworks[selectedDocument].documents.map(
                        (doc, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between p-2 bg-muted/20 rounded"
                          >
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm">{doc.name}</span>
                              <span className="text-xs text-muted-foreground">
                                ({doc.size})
                              </span>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() =>
                                  handleFrameworkDocDownload(doc.name, doc.id)
                                }
                                title="Preview document"
                              >
                                <Eye className="w-3 h-3" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() =>
                                  handleFrameworkDocDownload(doc.name, doc.id)
                                }
                                title="Download document"
                              >
                                <Download className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )} */}

                {/* Original document content for board documents */}
                {documentContent[selectedDocument] && (
                  <>
                    {/* Document Content */}
                    <div className="bg-muted/20 rounded-soft p-6">
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="text-lg font-semibold text-foreground">
                          {documentContent[selectedDocument].title}
                        </h3>
                        <Button
                          variant="outline"
                          className="flex items-center gap-2"
                          onClick={() => {
                            // Generate PDF from content
                            const content =
                              documentContent[selectedDocument].content;
                            const filename =
                              documentContent[selectedDocument].pdfName;

                            // Create a simple text file download (or integrate with PDF library later)
                            const blob = new Blob([content], {
                              type: "text/plain",
                            });
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement("a");
                            link.href = url;
                            link.download = filename.replace(".pdf", ".txt");
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                            window.URL.revokeObjectURL(url);

                            console.log("✅ Downloaded:", filename);
                          }}
                        >
                          <Download className="w-4 h-4" />
                          Download PDF
                        </Button>
                      </div>
                      <div className="text-sm text-foreground/80 whitespace-pre-line">
                        {documentContent[selectedDocument].content}
                      </div>
                    </div>

                    {/* Signature Status */}
                    <div>
                      <h4 className="text-lg font-semibold text-foreground mb-4">
                        Signature Status
                      </h4>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {boardMembers.map((member, index) => (
                          <div
                            key={index}
                            className="flex flex-col items-center space-y-2 p-3 bg-muted/10 rounded-soft"
                          >
                            <div className="relative">
                              <div className="w-16 h-16 rounded-full overflow-hidden">
                                <img
                                  src={member.image}
                                  alt={member.name}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              {member.signed && (
                                <div className="absolute -bottom-1 -right-1 bg-green-500 text-white rounded-full p-1">
                                  <Check className="w-3 h-3" />
                                </div>
                              )}
                            </div>
                            <div className="text-center">
                              <p className="text-xs font-medium text-foreground">
                                {member.name}
                              </p>
                              <p
                                className={`text-xs ${
                                  member.signed
                                    ? "text-green-600"
                                    : "text-muted-foreground"
                                }`}
                              >
                                {member.signed
                                  ? `Signed ${member.signedDate}`
                                  : "Pending"}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Executive Team Details Dialog */}
        <Dialog
          open={isExecutiveDialogOpen}
          onOpenChange={setIsExecutiveDialogOpen}
        >
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto bg-background">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <Users className="w-5 h-5 text-primary" />
                Executive Profile
              </DialogTitle>
              <DialogDescription>
                Detailed overview of executive responsibilities and
                organizational relationships.
              </DialogDescription>
            </DialogHeader>

            {selectedExecutive && executiveTeam[selectedExecutive] && (
              <div className="space-y-6 p-4">
                {/* Executive Header */}
                <div className="flex items-start gap-6">
                  <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0">
                    <img
                      src={executiveTeam[selectedExecutive].image}
                      alt={executiveTeam[selectedExecutive].name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      {executiveTeam[selectedExecutive].name}
                    </h3>
                    <p className="text-lg text-foreground/80 mb-2">
                      {executiveTeam[selectedExecutive].title}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-foreground/70">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>
                          Tenure: {executiveTeam[selectedExecutive].tenure}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Executive Details */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Responsibilities */}
                  <div>
                    <h4 className="font-semibold text-foreground border-b pb-2 mb-3">
                      Key Responsibilities
                    </h4>
                    <ul className="space-y-2">
                      {executiveTeam[selectedExecutive].responsibilities.map(
                        (responsibility, index) => (
                          <li
                            key={index}
                            className="text-sm text-foreground/80 flex items-start gap-2"
                          >
                            <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                            {responsibility}
                          </li>
                        )
                      )}
                    </ul>
                  </div>

                  {/* Authority & Reporting */}
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-foreground border-b pb-2 mb-3">
                        Decision Authority
                      </h4>
                      <p className="text-sm text-foreground/80">
                        {executiveTeam[selectedExecutive].authorityLevel}
                      </p>
                    </div>

                    <div>
                      <h4 className="font-semibold text-foreground border-b pb-2 mb-3">
                        Reporting Structure
                      </h4>
                      <div className="space-y-2">
                        <div className="text-sm">
                          <span className="font-medium">Reports to:</span>
                          <span className="ml-2 text-foreground/80">
                            {executiveTeam[selectedExecutive].reportsTo}
                          </span>
                        </div>
                        <div className="text-sm">
                          <span className="font-medium">Direct Reports:</span>
                          <ul className="ml-2 mt-1 space-y-1">
                            {executiveTeam[selectedExecutive].directReports.map(
                              (report, index) => (
                                <li
                                  key={index}
                                  className="text-foreground/80 text-xs"
                                >
                                  • {report}
                                </li>
                              )
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Add Role Dialog */}
        <Dialog open={showAddRoleDialog} onOpenChange={setShowAddRoleDialog}>
          <DialogContent className="max-w-2xl bg-background">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <Plus className="w-5 h-5 text-primary" />
                Add Executive Role
              </DialogTitle>
              <DialogDescription>
                Create a new C-level executive position for your organization
                structure.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 p-4">
              {/* Role Template */}
              <div className="bg-muted/20 rounded-soft p-6 border-2 border-dashed border-primary/30">
                <div className="flex flex-col items-center space-y-4">
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                    <Users className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <div className="text-center space-y-2">
                    <input
                      type="text"
                      placeholder="Role Title (e.g., Chief Innovation Officer)"
                      className="w-full text-center font-semibold bg-transparent text-foreground border-b border-primary/30 focus:border-primary outline-none placeholder:text-muted-foreground"
                    />
                    <input
                      type="text"
                      placeholder="Executive Name"
                      className="w-full text-center text-sm bg-transparent text-foreground border-b border-primary/30 focus:border-primary outline-none placeholder:text-muted-foreground"
                    />
                  </div>
                </div>
              </div>

              {/* Role Configuration */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">
                      Key Responsibilities
                    </label>
                    <textarea
                      placeholder="• Strategic innovation planning&#10;• Technology partnerships&#10;• R&D oversight&#10;• Future market analysis"
                      className="w-full mt-2 p-3 bg-muted/20 rounded border border-primary/20 focus:border-primary outline-none text-sm h-24 text-foreground placeholder:text-muted-foreground"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">
                      Decision Authority
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., Innovation investments up to €250K"
                      className="w-full mt-2 p-3 bg-muted/20 rounded border border-primary/20 focus:border-primary outline-none text-sm text-foreground placeholder:text-muted-foreground"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Reports To</label>
                    <select className="w-full mt-2 p-3 bg-muted/20 rounded border border-primary/20 focus:border-primary outline-none text-sm text-foreground">
                      <option>CEO</option>
                      <option>COO</option>
                      <option>Board of Directors</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">
                      Direct Reports
                    </label>
                    <textarea
                      placeholder="• Innovation Manager&#10;• R&D Director&#10;• Technology Partnerships Lead"
                      className="w-full mt-2 p-3 bg-muted/20 rounded border border-primary/20 focus:border-primary outline-none text-sm h-24 text-foreground placeholder:text-muted-foreground"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowAddRoleDialog(false)}
                >
                  Cancel
                </Button>
                <Button onClick={() => setShowAddRoleDialog(false)}>
                  Add Role
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Reorganize Dialog */}
        <Dialog
          open={showReorganizeDialog}
          onOpenChange={setShowReorganizeDialog}
        >
          <DialogContent className="max-w-4xl bg-background">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <Users className="w-5 h-5 text-primary" />
                Reorganize Executive Structure
              </DialogTitle>
              <DialogDescription>
                Drag and drop executive positions to reorganize the company
                structure.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 p-4">
              <div className="bg-muted/10 rounded-soft p-6">
                <div className="text-center mb-6">
                  <p className="text-sm text-foreground/70">
                    Drag executives to reposition them in the organizational
                    chart
                  </p>
                </div>

                {/* Reorganization Grid */}
                <div className="grid gap-4">
                  {/* CEO Level */}
                  <div className="flex justify-center">
                    <div className="w-32 h-24 bg-primary/10 rounded border-2 border-dashed border-primary/30 flex flex-col items-center justify-center cursor-move hover:bg-primary/20 transition-colors">
                      <Users className="w-6 h-6 text-primary mb-1" />
                      <span className="text-xs font-medium">CEO</span>
                    </div>
                  </div>

                  {/* C-Suite Grid */}
                  <div className="grid grid-cols-5 gap-4 mt-8">
                    {["COO", "CFO", "CTO", "CMO", "CHRO"].map((role) => (
                      <div
                        key={role}
                        className="w-24 h-20 bg-muted/20 rounded border-2 border-dashed border-muted/40 flex flex-col items-center justify-center cursor-move hover:bg-muted/30 transition-colors"
                      >
                        <Users className="w-5 h-5 text-muted-foreground mb-1" />
                        <span className="text-xs font-medium">{role}</span>
                      </div>
                    ))}
                  </div>

                  {/* Add New Position Area */}
                  <div className="flex justify-center mt-6">
                    <div className="w-32 h-24 bg-green-50 rounded border-2 border-dashed border-green-300 flex flex-col items-center justify-center cursor-pointer hover:bg-green-100 transition-colors">
                      <Plus className="w-6 h-6 text-green-600 mb-1" />
                      <span className="text-xs font-medium text-green-600">
                        Add Position
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowReorganizeDialog(false)}
                >
                  Cancel
                </Button>
                <Button onClick={() => setShowReorganizeDialog(false)}>
                  Save Changes
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Template Dialog */}
        <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
          <DialogContent className="max-w-5xl bg-background">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <Target className="w-5 h-5 text-primary" />
                Organization Structure Templates
              </DialogTitle>
              <DialogDescription>
                Choose from pre-designed organizational structures based on
                company size and industry.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 p-4">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Small Company Template */}
                <div className="border border-primary/20 rounded-soft p-4 hover:shadow-lg transition-shadow cursor-pointer">
                  <h4 className="font-semibold text-foreground mb-3">
                    Small Company (10-50 employees)
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-center">
                      <div className="bg-primary/10 px-3 py-1 rounded text-xs font-medium">
                        CEO
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="bg-muted/20 px-2 py-1 rounded text-center">
                        COO
                      </div>
                      <div className="bg-muted/20 px-2 py-1 rounded text-center">
                        CFO
                      </div>
                      <div className="bg-muted/20 px-2 py-1 rounded text-center">
                        CTO
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-foreground/70 mt-3">
                    Streamlined structure focusing on core operations, finance,
                    and technology.
                  </p>
                </div>

                {/* Medium Company Template */}
                <div className="border border-primary/20 rounded-soft p-4 hover:shadow-lg transition-shadow cursor-pointer">
                  <h4 className="font-semibold text-foreground mb-3">
                    Medium Company (50-200 employees)
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-center">
                      <div className="bg-primary/10 px-3 py-1 rounded text-xs font-medium">
                        CEO
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-xs">
                      <div className="bg-muted/20 px-2 py-1 rounded text-center">
                        COO
                      </div>
                      <div className="bg-muted/20 px-2 py-1 rounded text-center">
                        CFO
                      </div>
                      <div className="bg-muted/20 px-2 py-1 rounded text-center">
                        CTO
                      </div>
                      <div className="bg-muted/20 px-2 py-1 rounded text-center">
                        CMO
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-foreground/70 mt-3">
                    Balanced structure with dedicated marketing leadership and
                    operational focus.
                  </p>
                </div>

                {/* Large Company Template */}
                <div className="border border-primary/20 rounded-soft p-4 hover:shadow-lg transition-shadow cursor-pointer">
                  <h4 className="font-semibold text-foreground mb-3">
                    Large Company (200+ employees)
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-center">
                      <div className="bg-primary/10 px-3 py-1 rounded text-xs font-medium">
                        CEO
                      </div>
                    </div>
                    <div className="grid grid-cols-5 gap-1 text-xs">
                      <div className="bg-muted/20 px-2 py-1 rounded text-center">
                        COO
                      </div>
                      <div className="bg-muted/20 px-2 py-1 rounded text-center">
                        CFO
                      </div>
                      <div className="bg-muted/20 px-2 py-1 rounded text-center">
                        CTO
                      </div>
                      <div className="bg-muted/20 px-2 py-1 rounded text-center">
                        CMO
                      </div>
                      <div className="bg-muted/20 px-2 py-1 rounded text-center">
                        CHRO
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-foreground/70 mt-3">
                    Comprehensive C-suite with specialized human resources
                    leadership.
                  </p>
                </div>

                {/* Consolidated Firm Template */}
                <div className="border border-primary/20 rounded-soft p-4 hover:shadow-lg transition-shadow cursor-pointer">
                  <h4 className="font-semibold text-foreground mb-3">
                    Consolidated Firm
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-center">
                      <div className="bg-primary/10 px-2 py-1 rounded text-xs font-medium">
                        Group CEO
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs mb-2">
                      <div className="bg-muted/20 px-2 py-1 rounded text-center">
                        Division A CEO
                      </div>
                      <div className="bg-muted/20 px-2 py-1 rounded text-center">
                        Division B CEO
                      </div>
                      <div className="bg-muted/20 px-2 py-1 rounded text-center">
                        Division C CEO
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-1 text-xs">
                      <div className="bg-blue-100 px-1 py-1 rounded text-center text-blue-800">
                        COO
                      </div>
                      <div className="bg-blue-100 px-1 py-1 rounded text-center text-blue-800">
                        CFO
                      </div>
                      <div className="bg-blue-100 px-1 py-1 rounded text-center text-blue-800">
                        CTO
                      </div>
                      <div className="bg-blue-100 px-1 py-1 rounded text-center text-blue-800">
                        CLO
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-foreground/70 mt-3">
                    Multi-division structure with shared corporate functions and
                    legal oversight.
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowTemplateDialog(false)}
                >
                  Cancel
                </Button>
                <Button onClick={() => setShowTemplateDialog(false)}>
                  Apply Template
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Dashboard Notification */}
      <BoardDynamicsNotification
        isOpen={showDynamicsNotification}
        onClose={() => setShowDynamicsNotification(false)}
        onStartAssessment={() =>
          dispatchBusinessDashNavigate({
            tab: "governance",
            governanceSection: "chemistry",
            openChemistryAssessment: true,
          })
        }
      />

      {/* Form Dialogs for CRUD operations */}

      {/* Milestone Form Dialog */}
      <Dialog open={showMilestoneForm} onOpenChange={setShowMilestoneForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? "Edit Milestone" : "Add New Milestone"}
            </DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              const milestone = {
                name: formData.get("name"),
                status: formData.get("status"),
                date: formData.get("date"),
              };

              if (editingItem && editingItemType === "milestone") {
                handleEditMilestone(editingItem.index, milestone);
              } else {
                handleAddMilestone(milestone);
              }
              setShowMilestoneForm(false);
              setEditingItem(null);
            }}
          >
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Milestone Name</Label>
                <Input
                  id="name"
                  name="name"
                  defaultValue={editingItem?.name || ""}
                  required
                />
              </div>
              <div>
                <Label htmlFor="status">Status</Label>
                <Select
                  name="status"
                  defaultValue={editingItem?.status || "Planned"}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Planned">Planned</SelectItem>
                    <SelectItem value="In Progress">In Progress</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  name="date"
                  type="date"
                  defaultValue={editingItem?.date || ""}
                  required
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowMilestoneForm(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  {editingItem ? "Update" : "Add"} Milestone
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Meeting Form Dialog */}
      <Dialog open={showMeetingForm} onOpenChange={setShowMeetingForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? "Edit Meeting" : "Add New Meeting"}
            </DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              const meeting = {
                type: formData.get("type"),
                date: formData.get("date"),
              };

              if (editingItem && editingItemType === "meeting") {
                handleEditMeeting(editingItem.index, meeting);
              } else {
                handleAddMeeting(meeting);
              }
              setShowMeetingForm(false);
              setEditingItem(null);
            }}
          >
            <div className="space-y-4">
              <div>
                <Label htmlFor="type">Meeting Type</Label>
                <Input
                  id="type"
                  name="type"
                  defaultValue={editingItem?.type || ""}
                  required
                />
              </div>
              <div>
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  name="date"
                  type="date"
                  defaultValue={editingItem?.date || ""}
                  required
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowMeetingForm(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  {editingItem ? "Update" : "Add"} Meeting
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* KPI Form Dialog */}
      <Dialog open={showKpiForm} onOpenChange={setShowKpiForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? "Edit KPI" : "Add New KPI"}
            </DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              const kpi = {
                name: formData.get("name"),
                target: formData.get("target"),
                current: formData.get("current"),
              };

              if (editingItem && editingItemType === "kpi") {
                handleEditKpi(editingItem.index, kpi);
              } else {
                handleAddKpi(kpi);
              }
              setShowKpiForm(false);
              setEditingItem(null);
            }}
          >
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">KPI Name</Label>
                <Input
                  id="name"
                  name="name"
                  defaultValue={editingItem?.name || ""}
                  required
                />
              </div>
              <div>
                <Label htmlFor="target">Target</Label>
                <Input
                  id="target"
                  name="target"
                  defaultValue={editingItem?.target || ""}
                  required
                />
              </div>
              <div>
                <Label htmlFor="current">Current Value</Label>
                <Input
                  id="current"
                  name="current"
                  defaultValue={editingItem?.current || ""}
                  required
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowKpiForm(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  {editingItem ? "Update" : "Add"} KPI
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Committee Form Dialog */}
      <Dialog
        open={showAddCommitteeForm}
        onOpenChange={setShowAddCommitteeForm}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add New Committee</DialogTitle>
            <DialogDescription>
              Create a new committee with all necessary details including
              purpose, timeline, and objectives.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                const formData = new FormData(e.currentTarget);
                const participantNames = formData.getAll("participants");
                
                // Create the committee via API
                const newCommittee = await createCommittee({
                  name: formData.get("name"),
                  start_date: formData.get("startDate"),
                  end_date: formData.get("deadline"),
                  purpose: formData.get("purpose"),
                  is_active: true,
                });

                const committeeId = newCommittee.id || newCommittee.data?.id;

                // Add participants as committee members
                if (committeeId && participantNames.length > 0) {
                  // Find board member IDs from names
                  const memberMap = new Map();
                  boardMembers.forEach(member => {
                    if (member.id) {
                      memberMap.set(member.name, member.id);
                    }
                  });

                  for (const participantName of participantNames) {
                    const memberId = memberMap.get(participantName);
                    if (memberId) {
                      try {
                        await addCommitteeMember(committeeId, {
                          board_member_id: memberId,
                        });
                      } catch (err) {
                        console.warn("Failed to add committee member:", err);
                      }
                    }
                  }
                }

                // Update local state
                handleAddCommittee({
                  name: formData.get("name"),
                  startDate: formData.get("startDate"),
                  deadline: formData.get("deadline"),
                  purpose: formData.get("purpose"),
                  participants: participantNames,
                });

                setShowAddCommitteeForm(false);
                alert("Committee created successfully!");
              } catch (error) {
                console.error("Failed to create committee:", error);
                alert("Failed to create committee. Please try again.");
              }
            }}
          >
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Committee Name</Label>
                <Input
                  id="name"
                  name="name"
                  placeholder="e.g., Digital Transformation"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="startDate">Start Date</Label>
                  <Input id="startDate" name="startDate" type="date" required />
                </div>
                <div>
                  <Label htmlFor="deadline">Deadline</Label>
                  <Input id="deadline" name="deadline" type="date" required />
                </div>
              </div>
              <div>
                <Label htmlFor="participants">Select Participants</Label>
                <div className="max-h-48 overflow-auto border border-muted/40 rounded-md p-3 space-y-2">
                  {isLoadingBoardMembers ? (
                    <p className="text-sm text-muted-foreground">Loading members...</p>
                  ) : boardMembers.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No board members found.</p>
                  ) : (
                    boardMembers.map((member) => (
                      <label
                        key={member.name}
                        className="flex items-center gap-2 text-sm"
                      >
                        <input
                          type="checkbox"
                          name="participants"
                          value={member.name}
                          className="rounded"
                        />
                        <span>{member.name}</span>
                      </label>
                    ))
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Select board members to participate in this committee
                </p>
              </div>
              <div>
                <Label htmlFor="purpose">Purpose</Label>
                <Textarea
                  id="purpose"
                  name="purpose"
                  placeholder="Describe the committee's purpose and objectives..."
                  className="min-h-[100px]"
                  required
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddCommitteeForm(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">Create Committee</Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Document Viewer Dialog */}
      <Dialog open={showDocumentViewer} onOpenChange={setShowDocumentViewer}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-background">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-primary" />
              {viewingDocument?.name}
            </DialogTitle>
            <DialogDescription>
              Document viewer - {viewingDocument?.type?.toUpperCase()} (
              {viewingDocument?.size})
            </DialogDescription>
          </DialogHeader>

          {viewingDocument && (
            <div className="space-y-6 p-4">
              {/* Back button */}
              <div className="flex justify-start">
                <Button
                  variant="outline"
                  onClick={() => setShowDocumentViewer(false)}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </Button>
              </div>

              {/* Document Preview */}
              <div className="bg-muted/20 rounded-soft p-6 min-h-[400px] flex items-center justify-center">
                <div className="text-center space-y-4">
                  <div className="w-20 h-20 bg-primary/10 rounded-soft flex items-center justify-center mx-auto">
                    <FileText className="w-10 h-10 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      {viewingDocument.name}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      This is a preview of the document. In a real application,
                      this would show the actual document content.
                    </p>
                    <div className="space-y-2">
                      <p className="text-sm">
                        <strong>Type:</strong>{" "}
                        {viewingDocument.type?.toUpperCase()}
                      </p>
                      <p className="text-sm">
                        <strong>Size:</strong> {viewingDocument.size}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 justify-center">
                    <Button
                      variant="outline"
                      className="flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Board;
